import React, { createContext } from 'react'
import { Helmet } from 'react-helmet'
import { IntlProvider } from 'react-intl'
import arMessages from '../translations/ar.json'
import enMessages from '../translations/en.json'
import GoogleFonts, { ArabicFont, EnglishFont } from '../utils/Fonts'
import { useKeycloak } from '@react-keycloak/web'
import Configuration from '../Services/Api/Configuration'
import { LocalStorageService } from '../Services/LocalStorageService'

let { DefaultLocale, DefaultDirection, DefaultFont, DefaultMessage } = Configuration

const LanguageContext = createContext({})

const LanguageProvider = ({ children }) => {
  const [dir, setDir] = React.useState(DefaultDirection)
  const [locale, setLocale] = React.useState(DefaultLocale)
  const [font, setFont] = React.useState(DefaultFont)
  const [messages, setMessages] = React.useState(DefaultMessage)
  const [loading, setLoading] = React.useState(true)
  const { keycloak, initialized } = useKeycloak()

  // empty array as second argument equivalent to componentDidMount
  let isSubscribed = false
  React.useEffect(() => {
    GoogleFonts()
    // getAppLanguage()
    // eslint-disable-next-line
    return () => isSubscribed = false
  }, [])

  React.useEffect(() => {
      if (!!initialized) {
        // eslint-disable-next-line
        isSubscribed = true
        getAppLanguage()
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialized, keycloak?.tokenParsed?.locale])

  const switchToEnglish = (fromApp) => {
    setLoading(true)
    setMessages(enMessages)
    setLocale('en')
    setDir('ltr')
    setFont(EnglishFont)
    LocalStorageService.setMultiple({
      'EInvoicingLanguage': 'en',
      'EInvoicingDirection': 'ltr',
      'EInvoicingFontName': EnglishFont
    })
    if (!!fromApp) {
      LocalStorageService.set('EInvoicingLanguageSource', 'app')
    }
    setLoading(false)
  }

  const switchToArabic = (fromApp) => {
    setLoading(true)
    setMessages(arMessages)
    setLocale('ar')
    setDir('rtl')
    setFont(ArabicFont)
    LocalStorageService.setMultiple({
      'EInvoicingLanguage': 'ar',
      'EInvoicingDirection': 'rtl',
      'EInvoicingFontName': ArabicFont
    })
    if (!!fromApp) {
      LocalStorageService.set('EInvoicingLanguageSource', 'app')
    }
    setLoading(false)
  }


  const getAppLanguage = async () => {
    if (isSubscribed) {
      setLoading(true)
      let LangSource = await LocalStorageService.get('EInvoicingLanguageSource') ? await LocalStorageService.get('EInvoicingLanguageSource') : 'app'
      let lastKeyClockLang = await LocalStorageService.get('EInvoicingLastKeyClockLang') ? await LocalStorageService.get('EInvoicingLastKeyClockLang') : ''
      let lang = await LocalStorageService.get('EInvoicingLanguage') ? await LocalStorageService.get('EInvoicingLanguage') : locale
      if (!!keycloak?.tokenParsed?.locale) {
        LocalStorageService.setMultiple({
          'EInvoicingLanguageSource': 'app',
          'EInvoicingLastKeyClockLang': lang !== keycloak?.tokenParsed?.locale ? lang : keycloak?.tokenParsed?.locale
        })
        if (LangSource === 'keyClock' && keycloak?.tokenParsed?.locale !== lastKeyClockLang && lang !== keycloak?.tokenParsed?.locale) {
          lang = keycloak?.tokenParsed?.locale
          // LocalStorageService.set('EInvoicingLastKeyClockLang', lang)
        }
      }
      if (lang === 'ar') {
        switchToArabic()
      } else {
        switchToEnglish()
      }
      setLoading(false)
    }
  }

  const switchOnSelect = (value) => {
    value.target.value === 'ar' ? switchToArabic() : switchToEnglish()
  }

  let title = 'Invoicing'
  return (
    !!loading ? '' :
      <LanguageContext.Provider value={{
        locale, messages, dir, loading, font,
        switchToArabic,
        switchToEnglish,
        switchOnSelect
      }}
      >
        <Helmet>
          <html lang={locale} dir={dir}/>
          <body dir={dir}/>
          <title>{title}</title>
        </Helmet>
        <IntlProvider
          key={locale}
          locale={locale}
          messages={messages}
          defaultLocale={DefaultLocale}
        >
          {children}
        </IntlProvider>
      </LanguageContext.Provider>
  )
}


const LanguageConsumer = LanguageContext.Consumer

export { LanguageProvider, LanguageConsumer, LanguageContext }
